// Access token lives in memory only; the refresh token is an HttpOnly SameSite=Strict cookie
// scoped to /v1/auth, rotated on every refresh and sent with a CSRF header.
const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '') ?? '';

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(signedIn: boolean) => void>();

export class ApiError extends Error {
  constructor(public status: number, message: string, public code: string) {
    super(message);
  }
}

export function onAuthChange(fn: (signedIn: boolean) => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function setToken(token: string | null) {
  accessToken = token;
  for (const fn of listeners) fn(Boolean(token));
}

export async function refresh(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${BASE}/v1/auth/refresh`, { method: 'POST', credentials: 'include', headers: { 'X-Requested-With': 'fetch', 'Content-Type': 'application/json' }, body: '{}' });
      if (!res.ok) {
        setToken(null);
        return false;
      }
      setToken((await res.json()).access_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function request<T = any>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}), ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'Cannot reach the server. Check your connection.', 'network');
  }
  if (res.status === 401 && retry && !path.startsWith('/v1/auth/login') && (await refresh())) return request<T>(method, path, body, false);
  if (res.status === 401) setToken(null);
  const data = res.headers.get('content-type')?.includes('json') ? await res.json().catch(() => ({})) : await res.text();
  if (!res.ok) throw new ApiError(res.status, (data as any)?.error ?? `Request failed (${res.status})`, (data as any)?.code ?? 'error');
  return data as T;
}

/** Downloads an authenticated file (CSV, PNG, PDF) through a Blob URL. */
export async function download(path: string, filename: string) {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
  if (res.status === 401 && (await refresh())) return download(path, filename);
  if (!res.ok) throw new ApiError(res.status, 'Download failed', 'download');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function blobUrl(path: string): Promise<string> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
  if (!res.ok) throw new ApiError(res.status, 'Image unavailable', 'download');
  return URL.createObjectURL(await res.blob());
}

export async function login(email: string, password: string) {
  const data = await request<{ access_token: string; user: any }>('POST', '/v1/auth/login', { email, password, client: 'web' }, false);
  setToken(data.access_token);
  return data.user;
}

export async function register(body: { email: string; password: string; name: string; organization_name: string }) {
  const data = await request<{ access_token: string; user: any }>('POST', '/v1/auth/register', { ...body, client: 'web' }, false);
  setToken(data.access_token);
  return data.user;
}

export async function logout() {
  await request('POST', '/v1/auth/logout').catch(() => {});
  setToken(null);
}

export const isSignedIn = () => Boolean(accessToken);
export const setSession = (token: string) => setToken(token);
